import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_URL from "../utils/keys";
import jwtDecode from "jwt-decode";
import { SSE } from "sse.js";
import toast from "react-hot-toast";
import useLocalStorage from "use-local-storage";
import { getUserInfo } from "../services/api";

const WebDataContext = createContext();

const WebDataProvider = ({ children }) => {
  const navigation = useNavigate();

  // User States
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(false);

  const [continueFormID, setContinueFormID] = useState(null);

  // AI States
  const [selectedChat, setSelectedChat] = React.useState(null);
  const [messages, setMessages] = useState([]);

  // const defaultDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  // const [theme, setTheme] = useLocalStorage(
  //   "theme",
  //   defaultDark ? "dark" : "light"
  // );

  useEffect(() => {
    // const userToken = JSON.parse(localStorage.getItem("user_google_id"));
    const userToken = localStorage.getItem("projectToken");
    
    if (userToken) {
      // const decodedToken = jwtDecode(userToken);
      // const tokenExpiration = decodedToken.exp * 1000; // Convert expiration time to milliseconds
      // const currentTimestamp = new Date().getTime();

      // if (currentTimestamp > tokenExpiration) {
      //   // Token has expired, remove it from local storage
      //   localStorage.removeItem("token");
      //   setAccessToken(null); // Set access token in state to null or any other default value
      // } else {
      //   setAccessToken(userToken);
      // }
      setAccessToken(userToken);
    }
  }, [accessToken]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user-info`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        });
        setUser(response.data.user);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUser(null);
      }
    };

    const getUserInfoFunc = async () => {
      const actualUserToken = localStorage.getItem("userToken")
        if (!actualUserToken){
          return;
        }
      try {
        
        const response = await getUserInfo(actualUserToken);
        setUser(response);
      } catch (e) {
        console.log("User info error", e);
      }
    };
    if (accessToken) {
      getUserInfoFunc();
    } else {
      setUser(null);
    }
  }, [accessToken]);

  const logout = async () => {
    localStorage.removeItem("token");
    setUser(null);
    setAccessToken(null);
    navigation("/");
    window.location.reload();
  };
  // Fetching recent chats
  const [recentChats, setRecentChats] = useState([]);

  const messageContainerRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [showprompt, setShowPrompt] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isloading, setIsLoading] = useState(false);

  const [contextHistory, setContextHistory] = useState([]);
  const resultRef = useRef();

  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const [interactionCount, setInteractionCount] = useState(0); // Count interactions

  useEffect(() => {
    // Logic to determine when to show feedback form
    if (interactionCount > 0 && interactionCount % 5 === 0) {
      setShowFeedbackForm(true);
    }
  }, [interactionCount]);

  //chat
  //Modal open close Logic
  const [openDeleteChat, setOpenDeleteChat] = useState(null);
  const [isHovered, setIsHovered] = useState(null);
  //Sidebar open close Logic
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    const screenWidth = window.innerWidth;

    if (screenWidth < 1270) {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

    // Delete recent a chat
    const handleDeleteChat = async (chatId) => {
      try {
        await axios.delete(`${API_URL}/api/chat/${chatId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          withCredentials: true,
        });
        setSelectedChat(null);
        setMessages([]);
        fetchChats();
      } catch (error) {
        console.error(error);
      }
    };

  const fetchChats = async () => {
    try {
      // const response = await axios.get(`${API_URL}/api/chat`, {
      const response = await axios.get(`${API_URL}/api/chat/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        withCredentials: true,
      });

      const chats = response.data;

      setRecentChats(chats);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchChats();
    }
  }, [accessToken, selectedChat]);

  const handleCreateNewChat = () => {
    setSelectedChat(null);
    setMessages([]);
    closeSidebar();
  };
  const sendFeedback = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!feedback) {
      toast.error("Write your feedback");
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/ai-feedback`,
        {
          message: feedback,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          withCredentials: true,
        }
      );
      // Clear form after submission
      setFeedback("");
      setShowFeedbackForm(false);
      toast.success("Thanks for your feeeback");
      setSubmitting(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setShowFeedbackForm(false);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const transformMessages = () => {
      const transformedState = messages.flatMap((message) => {
        const { prompt, text } = message;

        const userObject = { role: "user", content: prompt.trim() };
        const assistantObject = {
          role: "assistant",
          content: text.slice(0, 200).trim(),
        };

        // return [userObject, assistantObject];
        return [userObject, assistantObject];
      });

      setContextHistory(transformedState);
    };
    if (messages) {
      transformMessages();
    }
  }, [messages]);

  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };

  const handleSaveMessage = async (
    prompt,
    text,
    chatId,
    sources,
    images,
    relatedSearches
  ) => {
    try {
      if (chatId && prompt && text) {
        const response = await axios.post(
          `${API_URL}/api/message/`,
          {
            prompt: prompt,
            text: text,
            chat: chatId,
            sources: sources,
            images: images,
            relatedSearches: relatedSearches,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            withCredentials: true,
          }
        );

        setShowPrompt("");
        setInteractionCount((prevCount) => prevCount + 1);
        // setShowFeedbackForm(true)
        const newMessage = response.data;
      } else {
        console.log("no data");
      }
    } catch (error) {
      console.error("Error creating message:", error);
    }
  };

  let callOqlous = async (prompt) => {
    if (prompt !== "") {
      setPrompt(prompt);
      setShowFeedbackForm(false);
      setIsLoading(true);
      resultRef.current = "";
      let newChatId;
      if (selectedChat === null) {
        setMessages([]);

        const response = await axios.post(
          `${API_URL}/api/chat/`,
          {
            chatName: prompt.slice(0, 50),
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            withCredentials: true,
          }
        );
        const newChat = response.data;
        newChatId = newChat._id;
        setSelectedChat(newChat?._id);
        setMessages([]);
      } else {
        newChatId = selectedChat;
      }

      const response = await axios.post(
        `${API_URL}/api/oqlous/llm/get-context`,
        {
          query: prompt.slice(0, 1200),
        },
        {
          withCredentials: true,
        }
      );

      setMessages([
        ...messages,
        {
          chat: newChatId,
          prompt: prompt,
          text: "",
          sources: response.data.contexts || [],
          relatedSearches: response.data.relatedSearches || [],
        },
      ]);
      setIsLoading(false);

      let url = `${API_URL}/api/oqlous/llm/call-gpt-azure`;
      // let url = `${API_URL}/api/oqlous/llm/call-groq`;
      const requestData = {
        contexts: response.data.contexts || [],
        history: contextHistory.slice(-10) || [],
        query: prompt,
      };

      let source = new SSE(url, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        payload: JSON.stringify(requestData),
      });

      source.addEventListener("message", (e) => {
        console.log("Chunk:",e, e.data)
        if (e.data != "[DONE]") {
          
          console.log("content", e)
          let payload = JSON.parse(e.data);
          let { content } = payload.choices[0]?.delta || {};
          if (content != undefined) {
            resultRef.current = resultRef.current + content;

            setMessages([
              ...messages,
              {
                chat: newChatId,
                prompt: prompt,
                text: resultRef.current + content,
                sources: response.data.contexts || [],
                relatedSearches: response.data.relatedSearches || [],
              },
            ]);
          }
        } else {
          source.close();
        }
      });

      source.addEventListener("readystatechange", (e) => {
        if (e.readyState >= 2) {
          setMessages([
            ...messages,
            {
              chat: newChatId,
              prompt: prompt,
              text: resultRef.current,
              sources: response.data.contexts || [],
              relatedSearches: response.data.relatedSearches || [],
            },
          ]);
          handleSaveMessage(
            prompt,
            resultRef.current,
            newChatId,
            response.data.contexts,
            response.data.images || [],
            response.data.relatedSearches || []
          );
        }
      });

      source.stream();
    } else {
      alert("Please insert a prompt!");
    }
  };

  return (
    <WebDataContext.Provider
      value={{
        accessToken,
        user,
        setUser,
        setAccessToken,
        logout,
        selectedChat,
        setSelectedChat,
        messages,
        setMessages,
        continueFormID,
        setContinueFormID,
        fetchChats,
        recentChats,
        setRecentChats,
        searchTerm,
        setSearchTerm,
        showprompt,
        setShowPrompt,
        callOqlous,
        scrollToBottom,
        prompt,
        setPrompt,
        isloading,
        setIsLoading,
        messageContainerRef,
        feedback,
        setFeedback,
        submitting,
        setSubmitting,
        sendFeedback,
        showFeedbackForm,
        setShowFeedbackForm,
        handleCreateNewChat,
        openDeleteChat,
        setOpenDeleteChat,
        isHovered,
        setIsHovered,
        isSidebarOpen,
        setIsSidebarOpen,
        handleToggleSidebar,
        closeSidebar,
        handleDeleteChat
        // theme,
        // setTheme,
      }}
    >
      {children}
    </WebDataContext.Provider>
  );
};

export const WebDataState = () => {
  return useContext(WebDataContext);
};

export default WebDataProvider;
